import { useState, useEffect } from "react";
import { useIdleTimer } from "react-idle-timer";
import { Button, Dialog, DialogTitle, DialogContent, DialogActions, Typography } from "@mui/material";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { logout } from "@/Auth/api/Logout/mutation";
import { styled } from "@mui/material/styles";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "@/redux/store";
import { fetchAuthSession, signOut } from "aws-amplify/auth";
// Store Redux
import { clearTokens } from "@/redux/actions";
import { actions } from "@/redux/reducers/User";
import { actions as actionsLocation } from "@/redux/reducers/Location";
import { useNavigate } from "react-router-dom";

const BootstrapDialog = styled(Dialog)(() => ({
	"& .MuiPaper-root": {
		borderRadius: "10px",
	},
}));

const twenty = 20; // 20 minutes
const threeHundred = 300; // 300 seconds;
const timeout = 1000 * 60 * (Number(import.meta.env.VITE_TIMEOUT_INACTIVITY_DIALOG) || twenty);
const initialRemainingTime = Number(import.meta.env.VITE_REMAINING_TIME_DIALOG) || threeHundred;

const formatTime = (remainingTime: number) => {
	if (remainingTime >= 60) {
		const minutes = Math.floor(remainingTime / 60);
		const seconds = remainingTime % 60;
		const formattedSeconds = seconds < 10 ? "0" + seconds : seconds;
		return `${minutes}:${formattedSeconds}`;
	}
	return `${remainingTime}`;
};

const InactivityDialog = () => {
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const queryClient = useQueryClient();
	const [open, setOpen] = useState(false);
	const [remainingTime, setRemainingTime] = useState(initialRemainingTime);
	const localUser = useSelector((state: RootState) => state.user.user);

	const { mutateAsync } = useMutation({
		mutationKey: ["logout"],
		mutationFn: async (input: { locationId: string; userId: string }) => logout(input),
	});

	const onIdle = () => {
		setOpen(true);
	};

	const onMessage = (messageText: string) => {
		if (messageText === "continue") {
			setInitialState();
		}
	};

	const { reset, message, getElapsedTime } = useIdleTimer({
		onIdle,
		onMessage,
		timeout,
		crossTab: true,
		leaderElection: true,
		syncTimers: 200,
	});

	const handleStayLoggedIn = () => {
		fetchAuthSession({ forceRefresh: true });
		setInitialState();
		message("continue");
	};

	const setInitialState = () => {
		setOpen(false); // Close modal
		setRemainingTime(initialRemainingTime); // Reset countdown
		reset(); // Reset idle timer
	};

	const handleLogout = async () => {
		setInitialState();
		const locationId = localUser?.locationId ?? "";
		const userId = localUser?._id ?? "";
		await mutateAsync({ locationId, userId });
		await signOut({ global: true });
		queryClient.clear();
		dispatch(actions.SET_USER({ user: null }));
		dispatch(actionsLocation.SET_LOCATION({ location: null }));
		dispatch(clearTokens());
		window.localStorage.clear();
		navigate("/login");
	};

	// Countdown effect inside the modal
	useEffect(() => {
		let timer: NodeJS.Timeout;
		if (open) {
			timer = setInterval(() => {
				const elapsedTime = getElapsedTime();
				const totalTimeout = timeout + initialRemainingTime * 1000;
				if (elapsedTime > totalTimeout) {
					handleLogout();
				}
				setRemainingTime(Math.floor((totalTimeout - elapsedTime) / 1000));
			}, 100);
		}
		return () => clearInterval(timer);
	}, [open]);

	return (
		<BootstrapDialog open={open} maxWidth="xs">
			<DialogTitle className="!font-montserrat !text-[15px] text-default !pt-[25px]">Are you still there?</DialogTitle>
			<DialogContent className="!font-sans !text-[14.5px] text-default !pt-[5px] max-h-[200px]">
				Your session will end soon due to inactivity. Respond to continue your session.
			</DialogContent>
			<DialogActions className="!pb-[18px] !flex !justify-center">
				<Button className="!capitalize !bg-primary !text-white !rounded-md w-[115px]" onClick={handleStayLoggedIn}>
					<Typography className="!font-sans !text-[14.5px]">Continue {formatTime(remainingTime)}</Typography>
				</Button>
			</DialogActions>
		</BootstrapDialog>
	);
};

export default InactivityDialog;
